/**
 * Maps technical/backend errors to safe, user-facing copy.
 * Never expose raw Convex, API, or stack messages to the UI.
 */

export type ErrorContext =
    | "forecast"
    | "satellite"
    | "space-weather"
    | "map"
    | "reports"
    | "settings"
    | "auth"
    | "submit-report"
    | "admin"
    | "locations"
    | "subscription"
    | "upload"
    | "generic";

export type SafeErrorMessage = {
    title: string;
    description: string;
    canRetry: boolean;
    isOffline?: boolean;
};

type ErrorKind =
    | "offline"
    | "auth"
    | "network"
    | "not_found"
    | "validation"
    | "upload_size"
    | "rate_limit"
    | "permission"
    | "server"
    | "unknown";

const CONTEXT_COPY: Record<
    ErrorContext,
    { title: string; description: string }
> = {
    forecast: {
        title: "Unable to load forecast",
        description:
            "We couldn't load aurora forecast data right now. Cached data may still be available if you've used the app recently.",
    },
    satellite: {
        title: "Unable to load solar wind data",
        description: "Satellite measurements are temporarily unavailable. Please try again shortly.",
    },
    "space-weather": {
        title: "Unable to load space weather",
        description: "Sunspot, CME, and magnetometer data couldn't be loaded right now.",
    },
    map: {
        title: "Unable to load map data",
        description: "Community reports and overlays couldn't be loaded. The map may show limited information.",
    },
    reports: {
        title: "Unable to load reports",
        description: "Your aurora reports couldn't be loaded. Please try again.",
    },
    settings: {
        title: "Something went wrong",
        description: "We couldn't complete that settings action. Please try again.",
    },
    auth: {
        title: "Sign-in required",
        description: "Please sign in to continue, then try again.",
    },
    "submit-report": {
        title: "Couldn't submit report",
        description: "Your report wasn't saved. Check your connection and try again.",
    },
    admin: {
        title: "Admin action failed",
        description: "That change couldn't be applied. Please try again.",
    },
    locations: {
        title: "Couldn't update location",
        description: "Your location couldn't be saved or removed. Please try again.",
    },
    subscription: {
        title: "Subscription unavailable",
        description: "Billing details couldn't be loaded right now. Please try again later.",
    },
    upload: {
        title: "Couldn't upload image",
        description: "The photo didn't upload. Try a smaller file or check your connection.",
    },
    generic: {
        title: "Something went wrong",
        description: "An unexpected error occurred. Please try again.",
    },
};

function isBrowserOffline(): boolean {
    return typeof navigator !== "undefined" && navigator.onLine === false;
}

function getErrorText(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    if (error && typeof error === "object" && "message" in error) {
        const msg = (error as { message: unknown }).message;
        if (typeof msg === "string") return msg;
    }
    return "";
}

function classifyError(error: unknown): ErrorKind {
    if (isBrowserOffline()) return "offline";

    const text = getErrorText(error).toLowerCase();
    const name =
        error instanceof Error ? error.name.toLowerCase() : "";

    if (
        text.includes("unauthorized") ||
        text.includes("not authenticated") ||
        text.includes("authentication") ||
        text.includes("must be logged in") ||
        text.includes("sign in")
    ) {
        return "auth";
    }

    if (
        text.includes("forbidden") ||
        text.includes("not allowed") ||
        text.includes("permission denied") ||
        text.includes("admin only")
    ) {
        return "permission";
    }

    if (
        text.includes("not found") ||
        name.includes("notfound")
    ) {
        return "not_found";
    }

    if (
        text.includes("rate limit") ||
        text.includes("too many") ||
        text.includes("429")
    ) {
        return "rate_limit";
    }

    if (
        text.includes("invalid") ||
        text.includes("validation") ||
        text.includes("required") ||
        (text.includes("argument") && !text.includes("arguments size"))
    ) {
        return "validation";
    }

    if (
        text.includes("too large") ||
        text.includes("size is too large") ||
        text.includes("exceeds maximum")
    ) {
        return "upload_size";
    }

    if (
        text.includes("network") ||
        text.includes("fetch failed") ||
        text.includes("failed to fetch") ||
        text.includes("connection") ||
        text.includes("timeout") ||
        text.includes("timed out") ||
        name.includes("networkerror")
    ) {
        return "network";
    }

    if (
        text.includes("convex") ||
        text.includes("server") ||
        text.includes("internal") ||
        text.includes("503") ||
        text.includes("502") ||
        text.includes("500")
    ) {
        return "server";
    }

    return "unknown";
}

function kindToMessage(kind: ErrorKind, context: ErrorContext): SafeErrorMessage {
    const base = CONTEXT_COPY[context] ?? CONTEXT_COPY.generic;

    switch (kind) {
        case "offline":
            return {
                title: "You're offline",
                description:
                    "Connect to the internet to refresh data. Previously cached forecasts may still appear.",
                canRetry: true,
                isOffline: true,
            };
        case "auth":
            return {
                title: "Sign-in required",
                description: "Please sign in to continue, then try again.",
                canRetry: false,
            };
        case "permission":
            return {
                title: "Access denied",
                description: "You don't have permission to perform this action.",
                canRetry: false,
            };
        case "not_found":
            return {
                title: "Not found",
                description: "That item may have been removed or is no longer available.",
                canRetry: false,
            };
        case "validation":
            return {
                title: "Couldn't complete request",
                description: "Some information was missing or invalid. Review your entries and try again.",
                canRetry: false,
            };
        case "upload_size":
            return {
                title: CONTEXT_COPY.upload.title,
                description: CONTEXT_COPY.upload.description,
                canRetry: true,
            };
        case "rate_limit":
            return {
                title: "Too many requests",
                description: "Please wait a moment before trying again.",
                canRetry: true,
            };
        case "network":
            return {
                title: "Connection problem",
                description:
                    "We couldn't reach the server. Check your connection and try again.",
                canRetry: true,
            };
        case "server":
            return {
                title: base.title,
                description: base.description,
                canRetry: true,
            };
        default:
            return {
                title: base.title,
                description: base.description,
                canRetry: true,
            };
    }
}

/** Log full error for developers; never shown to users. */
export function logSafeError(error: unknown, context?: string): void {
    console.error(`[aurora-forecast${context ? `:${context}` : ""}]`, error);
}

/** Default safe message for a context (no logging). */
export function getDefaultErrorForContext(context: ErrorContext): SafeErrorMessage {
    const base = CONTEXT_COPY[context] ?? CONTEXT_COPY.generic;
    return { title: base.title, description: base.description, canRetry: true };
}

/** Convert any thrown value into safe user-facing copy. */
export function getSafeErrorMessage(
    error: unknown,
    context: ErrorContext = "generic",
    options?: { log?: boolean },
): SafeErrorMessage {
    if (options?.log !== false) {
        logSafeError(error, context);
    }
    return kindToMessage(classifyError(error), context);
}

/** Run async work; returns undefined on failure after setting safe message via callback. */
export async function runSafeAsync<T>(
    fn: () => Promise<T>,
    context: ErrorContext,
    onError?: (message: SafeErrorMessage) => void,
): Promise<T | undefined> {
    try {
        return await fn();
    } catch (error) {
        onError?.(getSafeErrorMessage(error, context));
        return undefined;
    }
}
